// src/features/admin/components/settings/document-management/components/ArchivedDocuments.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  DocumentDuplicateIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useDocumentAPI } from '../../../../../dms/DocumentAPI';

// Types for archived documents
interface ArchivedDocument {
  id: string;
  document_id?: string; // Backend property name for document ID
  title: string;
  document_number: string;
  displayNumber: string;
  document_type: string;
  practice_area: string;
  original_author: string;
  archived_date: Date | string;  // Allow both Date objects and date strings
  archived_by: string;
  archive_reason: string;
  archive_notes?: string;
  file_size?: number;
  client_name?: string;
  matter_number?: string;
}

interface ArchivedDocumentsProps {
  className?: string;
}


// Archive reasons for filtering
const ARCHIVE_REASONS = [
  'Document Finalized',
  'Case Closed',
  'Client Services Completed',
  'Document Superseded',
  'Retention Policy',
  'Client Request',
  'Other'
];

// Document types for filtering
const DOCUMENT_TYPES = [
  'Contract',
  'Pleading',
  'Correspondence',
  'Form',
  'Research',
  'Discovery',
  'Other'
];

const readApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.clone().json();
    if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
    if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error;
    if (typeof payload?.description === 'string' && payload.description.trim()) return payload.description;
  } catch {
    try {
      const text = await response.text();
      if (text.trim()) return text;
    } catch {
      // Use fallback below.
    }
  }

  return `${fallback}: ${response.status} ${response.statusText}`;
};

export const ArchivedDocuments: React.FC<ArchivedDocumentsProps> = ({ className = '' }) => {
  const documentAPI = useDocumentAPI();
  
  // State management
  const [documents, setDocuments] = useState<ArchivedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('');
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string>('');
  const [selectedArchiveReason, setSelectedArchiveReason] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Selection and actions
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [restoreLoading, setRestoreLoading] = useState<Set<string>>(new Set());
  const [deleteLoading, setDeleteLoading] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load archived documents
  const loadArchivedDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 [ArchivedDocuments] Loading archived documents...');
      
      // Call the archived documents API
      const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/archived`, {
        headers: documentAPI.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Failed to load archived documents'));
      }

      const result = await response.json();
      console.log('✅ [ArchivedDocuments] Archived documents loaded:', result);
      
      // Transform backend data to frontend format
      const transformedDocuments: ArchivedDocument[] = (result.data || []).map((doc: any) => ({
        id: doc.document_id,
        document_id: doc.document_id,
        title: doc.title,
        document_number: doc.document_id.slice(0, 8).toUpperCase(),
        displayNumber: doc.document_id.slice(0, 8).toUpperCase(),
        document_type: doc.primary_type || 'Document',
        practice_area: doc.practice_area || 'General',
        original_author: doc.created_by_user_id || 'Unknown',
        archived_date: doc.archived_at || doc.updated_at,
        archived_by: doc.archived_by_user_id || 'Unknown',
        archive_reason: doc.archive_reason || 'No reason provided',
        archive_notes: doc.archive_notes || '',
        file_size: doc.file_size || 0,
        client_name: doc.client_name || 'Unknown Client',
        matter_number: doc.matter_id || 'N/A'
      }));
      
      setDocuments(transformedDocuments);
    } catch (err) {
      console.error('❌ [ArchivedDocuments] Error loading archived documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load archived documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [documentAPI]);

  // Initial load
  useEffect(() => {
    loadArchivedDocuments();
  }, [loadArchivedDocuments]);

  // Clear success message after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Filter documents based on search criteria
  const filteredDocuments = documents.filter(document => {
    const matchesSearch = searchTerm === '' || 
      document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      document.document_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      document.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      document.original_author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDocumentType = selectedDocumentType === '' || document.document_type === selectedDocumentType;
    const matchesPracticeArea = selectedPracticeArea === '' || document.practice_area === selectedPracticeArea;
    const matchesArchiveReason = selectedArchiveReason === '' || document.archive_reason === selectedArchiveReason;
    
    const matchesDateRange = (() => {
      if (!dateRange.start && !dateRange.end) return true;
      const docDate = new Date(document.archived_date);
      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;
      
      if (startDate && docDate < startDate) return false;
      if (endDate && docDate > endDate) return false;
      return true;
    })();
    
    return matchesSearch && matchesDocumentType && matchesPracticeArea && matchesArchiveReason && matchesDateRange;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, startIndex + itemsPerPage);

  // Get unique values for filter dropdowns
  const uniquePracticeAreas = [...new Set(documents.map(d => d.practice_area))];

  // Handle document selection
  const handleSelectDocument = (documentId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedDocuments);
    if (isSelected) {
      newSelected.add(documentId);
    } else {
      newSelected.delete(documentId);
    }
    setSelectedDocuments(newSelected);
  };

  // Handle select all
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      setSelectedDocuments(new Set(paginatedDocuments.map(d => d.id)));
    } else {
      setSelectedDocuments(new Set());
    }
  };

  const permanentlyDeleteArchivedDocument = async (documentId: string) => {
    const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/${documentId}/permanent`, {
      method: 'DELETE',
      headers: documentAPI.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(await readApiErrorMessage(response, `Failed to permanently delete document ${documentId}`));
    }
  };

  // Restore document from archive
  const handleRestoreDocument = async (documentId: string) => {
    try {
      setRestoreLoading(prev => new Set([...prev, documentId]));
      setError(null);
      
      console.log('🔍 [ArchivedDocuments] Restoring document:', documentId);

      // Call the restore API
      const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/${documentId}/restore`, {
        method: 'POST',
        headers: documentAPI.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Failed to restore document'));
      }

      console.log('✅ [ArchivedDocuments] Document restored successfully');
      
      // Remove from archived documents list
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
      
      setSuccessMessage('Document restored successfully.');
    } catch (err) {
      console.error('❌ [ArchivedDocuments] Failed to restore document:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore document. Please try again.');
    } finally {
      setRestoreLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  // Permanently delete document
  const handleDeleteDocument = async (documentId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(prev => new Set([...prev, documentId]));
      setError(null);
      
      console.log('🔍 [ArchivedDocuments] Permanently deleting archived document:', documentId);

      await permanentlyDeleteArchivedDocument(documentId);

      console.log('✅ [ArchivedDocuments] Archived document permanently deleted:', documentId);
      
      // Remove from documents list
      setDocuments(prev => prev.filter(d => d.id !== documentId));
      setSelectedDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
      
      setSuccessMessage('Document permanently deleted.');
    } catch (err) {
      console.error('❌ [ArchivedDocuments] Failed to permanently delete document:', err);
      setError(err instanceof Error ? err.message : 'Failed to permanently delete document. Please try again.');
    } finally {
      setDeleteLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  // Bulk actions
  const handleBulkRestore = async () => {
    if (selectedDocuments.size === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const documentIds = Array.from(selectedDocuments);
      await Promise.all(documentIds.map(async (documentId) => {
        const response = await fetch(`${documentAPI.getBaseUrl()}/api/v1/dms/documents/${documentId}/restore`, {
          method: 'POST',
          headers: documentAPI.getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error(await readApiErrorMessage(response, `Failed to restore document ${documentId}`));
        }
      }));
      
      // Remove selected documents from list
      setDocuments(prev => prev.filter(d => !documentIds.includes(d.id)));
      setSelectedDocuments(new Set());
      
      setSuccessMessage(`${documentIds.length} document(s) restored successfully.`);
    } catch (err) {
      console.error('❌ [ArchivedDocuments] Failed to restore selected documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore selected documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.size === 0) return;

    const documentIds = Array.from(selectedDocuments);
    const selectedCount = documentIds.length;
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${selectedCount} selected archived document${selectedCount === 1 ? '' : 's'}? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setError(null);
      setDeleteLoading(prev => new Set([...prev, ...documentIds]));

      const results = await Promise.allSettled(
        documentIds.map((documentId) => permanentlyDeleteArchivedDocument(documentId))
      );

      const deletedIds: string[] = [];
      const failedMessages: string[] = [];
      results.forEach((result, index) => {
        const documentId = documentIds[index];
        if (result.status === 'fulfilled') {
          deletedIds.push(documentId);
        } else {
          failedMessages.push(result.reason instanceof Error ? result.reason.message : `Failed to delete document ${documentId}`);
        }
      });

      if (deletedIds.length > 0) {
        setDocuments(prev => prev.filter(d => !deletedIds.includes(d.id)));
        setSelectedDocuments(prev => {
          const newSet = new Set(prev);
          deletedIds.forEach(id => newSet.delete(id));
          return newSet;
        });
      }

      if (failedMessages.length > 0) {
        const failureSummary = `${failedMessages.length} of ${selectedCount} selected document${selectedCount === 1 ? '' : 's'} failed to delete.`;
        setError(`${failureSummary} ${failedMessages[0]}`);
        if (deletedIds.length > 0) {
          setSuccessMessage(`${deletedIds.length} document(s) permanently deleted.`);
        }
        return;
      }

      setSelectedDocuments(new Set());
      setSuccessMessage(`${deletedIds.length} document(s) permanently deleted.`);
    } catch (err) {
      console.error('❌ [ArchivedDocuments] Failed to permanently delete selected documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to permanently delete selected documents. Please try again.');
    } finally {
      setDeleteLoading(prev => {
        const newSet = new Set(prev);
        documentIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDocumentType('');
    setSelectedPracticeArea('');
    setSelectedArchiveReason('');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
  };

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (date: Date | string): string => {
    const d = new Date(date);
    return d.toLocaleDateString();
  };

  // Loading state
  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading archived documents...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!loading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <ArchiveBoxIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No Archived Documents
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Documents that have been archived will appear here.
        </p>
        <button
          onClick={loadArchivedDocuments}
          className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover flex items-center space-x-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          <span>Refresh</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ArchiveBoxIcon className="h-6 w-6 text-yellow-600 mr-2" />
            Archived Documents
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage and restore archived documents ({filteredDocuments.length} documents)
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {selectedDocuments.size > 0 && (
            <>
              <button
                onClick={handleBulkDelete}
                disabled={deleteLoading.size > 0}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <TrashIcon className="h-4 w-4" />
                <span>Delete Selected ({selectedDocuments.size})</span>
              </button>
              <button
                onClick={handleBulkRestore}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Restore Selected ({selectedDocuments.size})</span>
              </button>
            </>
          )}
          <button
            onClick={loadArchivedDocuments}
            disabled={loading}
            className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50 flex items-center space-x-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            <p className="text-sm text-green-700 dark:text-green-300">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {/* Search */}
          <div className="xl:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search Documents
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, number, client, or author..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Document Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Type
            </label>
            <select
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Types</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Practice Area Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Practice Area
            </label>
            <select
              value={selectedPracticeArea}
              onChange={(e) => setSelectedPracticeArea(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Areas</option>
              {uniquePracticeAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>

          {/* Archive Reason Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Archive Reason
            </label>
            <select
              value={selectedArchiveReason}
              onChange={(e) => setSelectedArchiveReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Reasons</option>
              {ARCHIVE_REASONS.map(reason => (
                <option key={reason} value={reason}>{reason}</option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500 flex items-center justify-center space-x-2"
            >
              <XMarkIcon className="h-4 w-4" />
              <span>Clear</span>
            </button>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={paginatedDocuments.length > 0 && paginatedDocuments.every(d => selectedDocuments.has(d.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Client/Matter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Archived
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedDocuments.map((document) => (
                <tr key={document.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedDocuments.has(document.id)}
                      onChange={(e) => handleSelectDocument(document.id, e.target.checked)}
                      className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <DocumentDuplicateIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {document.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {document.document_number} • {formatFileSize(document.file_size)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                      {document.document_type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {document.client_name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {document.matter_number}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatDate(document.archived_date)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      by {document.archived_by}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleRestoreDocument(document.id)}
                      disabled={restoreLoading.has(document.id)}
                      className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                      title="Restore document"
                    >
                      {restoreLoading.has(document.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      ) : (
                        <ArrowPathIcon className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(document.id)}
                      disabled={deleteLoading.has(document.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                      title="Permanently delete document"
                    >
                      {deleteLoading.has(document.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <XMarkIcon className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredDocuments.length)} of {filteredDocuments.length} documents
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded disabled:opacity-50 dark:bg-gray-600 dark:text-gray-300"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivedDocuments;
